import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReportLog } from "../../Models";
import useApiToken from "../../hooks/useApiToken";
import { FilterBadge, FilterChips } from "../Shared/Filter/ActiveFilterDisplay";
import {
  MagnifyingGlassIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowPathIcon,
  PlusIcon,
  MinusIcon
} from "@heroicons/react/20/solid";
import { Disclosure } from '@headlessui/react';  // ADD THIS
import { useActiveFilters, FilterEntry } from "../../hooks/useActiveFilters";
import ReactDatePicker from "react-datepicker";
import Notification from "../Shared/Notification";
import Loading from "../Shared/Loading";
import IssueRow from "./IssueRow";
import IssueRightSideBar from "./IssueRightSideBar";
import { PopUp } from "../../Models";
import ConfirmModal from "../Shared/ConfirmModal";
import { deleteReportLog, fetchReportLogCategories, fetchReportLogRoles, fetchReportLogs, updateReportLogStatus } from "../../api/masterdata.api";

interface Filters {
  search: string;
  startDate: string;
  endDate: string;
  sortBy: "timestamp" | "email" | "title";
  sortOrder: "asc" | "desc";
  status: string[];
  categories: string[];
  roles: string[];
}

function Issues() {
  const token = useApiToken();
  const [issues, setIssues] = useState<ReportLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    startDate: "",
    endDate: "",
    sortBy: "timestamp",
    sortOrder: "desc",
    status: ["all"],
    categories: ["all"],
    roles: ["all"]
  });
  const [notificationPopUp, setNotificationPopUp] = useState<PopUp>({
    title: "",
    description: "",
    type: "info"
  });
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [issueToDelete, setIssueToDelete] = useState<ReportLog | null>(null);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState<boolean>(false);

  const filterEntries = useMemo<FilterEntry[]>(() => {
    const entries: FilterEntry[] = [];
    if (!filters.status?.includes('all'))
      entries.push({ label: 'Status', active: true, displayValue: filters.status.join(', ') });
    if (!filters.categories?.includes('all'))
      entries.push({ label: 'Category', active: true, displayValue: filters.categories.join(', ') });
    if (!filters.roles?.includes('all'))
      entries.push({ label: 'Role', active: true, displayValue: filters.roles.join(', ') });
    if (filters.startDate || filters.endDate)
      entries.push({ label: 'Date', active: true, displayValue: 'custom range' });
    return entries;
  }, [filters]);

  const { activeFilterCount, activeFilterNames } = useActiveFilters(filterEntries);

  const loadIssues = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetchReportLogs(
        token,
        page,
        pageSize,
        filters.search,
        filters.startDate,
        filters.endDate,
        filters.sortBy,
        filters.sortOrder,
        
        // NEW FILTER PARAMETERS
        filters.status,
        filters.categories,
        filters.roles
      );
      
      if (response) {
        setIssues(response.records || []);
        setTotalRecords(response.totalRecords || 0);
      }
    } catch (error) {
      console.error("Error fetching issues:", error);
      setNotificationPopUp({
        title: "Error",
        description: "Failed to load reported issues",
        type: "error"
      });
      setShowNotification(true);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, filters]);
  // Load filter options on mount
useEffect(() => {
  const loadFilterOptions = async () => {
    if (!token) return;
    
    try {
      const [categories, roles] = await Promise.all([
        fetchReportLogCategories(token),
        fetchReportLogRoles(token)
      ]);
      
      setAvailableCategories(categories);
      setAvailableRoles(roles);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  loadFilterOptions();
}, [token]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: e.target.value });
    setPage(1);
  };

  // Same pattern as RoleLeftSideBar
const handleFilterChange = (sectionName: string, id: string) => {
  setFilters((prevFilters: any) => {
    let newSelected = [...prevFilters[sectionName]];
    
    if (id === 'all') {
      newSelected = ['all'];
    } else {
      // Remove 'all' if present
      newSelected = newSelected.filter(item => item !== 'all');
      
      // Toggle the selected item
      if (newSelected.includes(id)) {
        newSelected = newSelected.filter(item => item !== id);
      } else {
        newSelected.push(id);
      }
      
      // If nothing selected, default to 'all'
      if (newSelected.length === 0) {
        newSelected = ['all'];
      }
    }
    
    return {
      ...prevFilters,
      [sectionName]: newSelected,
    };
  });
  setPage(1);
};

const handleResetFilters = () => {
  setFilters({
    search: "",
    startDate: "",
    endDate: "",
    sortBy: "timestamp",
    sortOrder: "desc",
    status: ["all"],
    categories: ["all"],
    roles: ["all"]
  });
  setPage(1);
};

  const handleSortChange = (sortBy: "timestamp" | "email" | "title") => {
    setFilters({
      ...filters,
      sortBy,
      sortOrder: filters.sortBy === sortBy && filters.sortOrder === "asc" ? "desc" : "asc"
    });
    setPage(1);
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  const currentStartDate = filters.startDate ? new Date(filters.startDate) : null;
  const currentEndDate = filters.endDate ? new Date(filters.endDate) : null;

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    setFilters((prev) => ({
      ...prev,
      startDate: start ? start.toISOString() : "",
      endDate: end ? end.toISOString() : ""
    }));

    // Whenever the range changes, reset to first page;
    // effect on [filters, page] will trigger data reload.
    setPage(1);
  };

  const handleRequestDeleteIssue = (issue: ReportLog) => {
    setIssueToDelete(issue);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteIssue = async () => {
    if (!token || !issueToDelete) return;

    try {
      await deleteReportLog(token, issueToDelete.id);
      setIssues((prev) => prev.filter((issue) => issue.id !== issueToDelete.id));
      setTotalRecords((prev) => Math.max(0, prev - 1));
      setNotificationPopUp({
        title: "Deleted",
        description: "The issue has been deleted successfully.",
        type: "success"
      });
      setDeleteModalOpen(false);
      setIssueToDelete(null);
    } catch (error) {
      console.error("Error deleting issue:", error);
      setNotificationPopUp({
        title: "Error",
        description: "Failed to delete the issue. Please try again.",
        type: "error"
      });
    } finally {
      setShowNotification(true);
    }
  };

  const handleStatusChange = async (id: string, newStatus: "open" | "closed") => {
    if (!token) return;

    try {
      await updateReportLogStatus(token, id, newStatus);

      setIssues((prev) =>
        prev.map((issue) =>
          issue.id === id ? { ...issue, status: newStatus } : issue
        )
      );
    } catch (error) {
      console.error("Error updating issue status:", error);
      setNotificationPopUp({
        title: "Error",
        description: "Failed to update issue status. Please try again.",
        type: "error",
      });
      setShowNotification(true);
    }
  };

  const handleSelect = (issueId: string) => {
    setSelectedIssues(prev =>
      prev.includes(issueId)
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIssues(issues.map(issue => issue.id));
    } else {
      setSelectedIssues([]);
    }
  };

  const handleToggleRightSidebar = (isOpen: boolean) => {
    setIsRightSidebarOpen(isOpen);
  };

  const handleBulkDelete = async () => {
    if (!token) return;

    try {
      for (const issueId of selectedIssues) {
        await deleteReportLog(token, issueId);
      }
      setIssues(prev => prev.filter(issue => !selectedIssues.includes(issue.id)));
      setTotalRecords(prev => Math.max(0, prev - selectedIssues.length));
      setNotificationPopUp({
        title: "Deleted",
        description: `${selectedIssues.length} issue${selectedIssues.length > 1 ? 's' : ''} deleted successfully.`,
        type: "success"
      });
      setSelectedIssues([]);
    } catch (error) {
      console.error("Error deleting issues:", error);
      setNotificationPopUp({
        title: "Error",
        description: "Failed to delete some issues. Please try again.",
        type: "error"
      });
    } finally {
      setShowNotification(true);
    }
  };

  const handleBulkClose = async () => {
    if (!token) return;

    try {
      for (const issueId of selectedIssues) {
        await updateReportLogStatus(token, issueId, "closed");
      }
      setIssues(prev =>
        prev.map(issue =>
          selectedIssues.includes(issue.id) ? { ...issue, status: "closed" } : issue
        )
      );
      setNotificationPopUp({
        title: "Updated",
        description: `${selectedIssues.length} issue${selectedIssues.length > 1 ? 's' : ''} closed successfully.`,
        type: "success"
      });
      setSelectedIssues([]);
    } catch (error) {
      console.error("Error closing issues:", error);
      setNotificationPopUp({
        title: "Error",
        description: "Failed to close some issues. Please try again.",
        type: "error"
      });
    } finally {
      setShowNotification(true);
    }
  };

  useEffect(() => {
    if (selectedIssues.length > 0) {
      setIsRightSidebarOpen(true);
      setIsSidebarOpen(false);
    } else {
      setIsRightSidebarOpen(false);
      setIsSidebarOpen(true);
    }
  }, [selectedIssues]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-darkbg">
      <div className="relative flex">
        {/* Left sidebar */}
        
        <aside
  className={`select-none fixed z-10 top-[4.1rem] block h-[calc(99vh-4rem)] left-0 shadow-md border dark:border-gray-500 transition-transform transform duration-200 w-[19rem] ${
    isSidebarOpen ? "translate-x-0" : "-translate-x-[100%]"
  }`}
>
  {/* Header */}
  <div className="mx-auto bg-gray-100 dark:bg-darkbglight">
    <div className="min-w-0 px-4 pt-2 pb-1">
      <div className="text-gray-900 dark:text-gray-300 flex items-center space-x-2 justify-center">
        <h3 className="text-center text-lg font-semibold leading-7 sm:text-base sm:tracking-tight">
          Issue Filters
        </h3>
      </div>
    </div>
  </div>

  {/* Toggle button */}
  <div
    className={`absolute top-10 ${
      isSidebarOpen ? "-right-2 shadow-md" : "-right-4 shadow-xl"
    } h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
  >
    {isSidebarOpen ? (
      <ChevronDoubleLeftIcon width={16} />
    ) : (
      <ChevronDoubleRightIcon width={16} />
    )}
  </div>

  {/* Sidebar Content */}
  <div className="pb-12 h-full bg-white thin-scroll overflow-y-auto dark:bg-darkbg w-full flex flex-col items-center mt-2">
    
    {/* Search Bar with Reset */}
    <div className="relative flex focus-within:z-10 mt-2 items-center justify-between w-[90%] mb-4">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
        <MagnifyingGlassIcon
          className="h-5 w-5 text-gray-400 dark:text-gray-300"
          aria-hidden="true"
        />
      </div>
      <input
        type="text"
        name="search"
        id="search"
        className="block w-10/12 border-0 pl-9 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
        placeholder="Quick Search"
        onChange={handleSearchChange}
        value={filters.search}
      />
      <div
        className="text-gray-400 hover:text-gray-500 cursor-pointer relative dark:hover:text-gray-300"
        onClick={handleResetFilters}
        title="Reset all filters"
      >
        <FunnelIcon width={24} />
        <ArrowPathIcon width={14} className="absolute bottom-0 -right-1" />
        <FilterBadge count={activeFilterCount} />
      </div>
    </div>
    <FilterChips names={activeFilterNames} />

    {/* Status Filter */}
    <Disclosure as="div" className="w-[90%] border-b border-gray-200 dark:border-gray-500">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
            <span className="text-gray-900 dark:text-gray-400">Status</span>
            {open ? (
              <MinusIcon className="h-5 w-5 text-gray-200" />
            ) : (
              <PlusIcon className="h-5 w-5 text-gray-200" />
            )}
          </Disclosure.Button>
          <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
            {[
              { id: 'all', name: 'All' },
              { id: 'open', name: 'Open' },
              { id: 'closed', name: 'Closed' }
            ].map((option) => (
              <div key={option.id} className="flex items-center mb-2">
                <input
                  id={`status-${option.id}`}
                  name="status[]"
                  type="checkbox"
                  checked={filters.status.includes(option.id)}
                  onChange={() => handleFilterChange('status', option.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor={`status-${option.id}`}
                  className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                >
                  {option.name}
                </label>
              </div>
            ))}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>

    {/* Category Filter */}
    <Disclosure as="div" className="w-[90%] border-b border-gray-200 dark:border-gray-500">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
            <span className="text-gray-900 dark:text-gray-400">Category</span>
            {open ? (
              <MinusIcon className="h-5 w-5 text-gray-200" />
            ) : (
              <PlusIcon className="h-5 w-5 text-gray-200" />
            )}
          </Disclosure.Button>
          <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
            <div key="category-all" className="flex items-center mb-2">
              <input
                id="category-all"
                name="categories[]"
                type="checkbox"
                checked={filters.categories.includes('all')}
                onChange={() => handleFilterChange('categories', 'all')}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label
                htmlFor="category-all"
                className="ml-3 text-sm text-gray-600 dark:text-gray-400"
              >
                All
              </label>
            </div>
            {availableCategories.map((category) => (
              <div key={category} className="flex items-center mb-2">
                <input
                  id={`category-${category}`}
                  name="categories[]"
                  type="checkbox"
                  checked={filters.categories.includes(category)}
                  onChange={() => handleFilterChange('categories', category)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor={`category-${category}`}
                  className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                >
                  {category}
                </label>
              </div>
            ))}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>

    {/* Role Filter */}
    <Disclosure as="div" className="w-[90%] border-b border-gray-200 dark:border-gray-500">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
            <span className="text-gray-900 dark:text-gray-400">Role</span>
            {open ? (
              <MinusIcon className="h-5 w-5 text-gray-200" />
            ) : (
              <PlusIcon className="h-5 w-5 text-gray-200" />
            )}
          </Disclosure.Button>
          <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
            <div key="role-all" className="flex items-center mb-2">
              <input
                id="role-all"
                name="roles[]"
                type="checkbox"
                checked={filters.roles.includes('all')}
                onChange={() => handleFilterChange('roles', 'all')}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label
                htmlFor="role-all"
                className="ml-3 text-sm text-gray-600 dark:text-gray-400"
              >
                All
              </label>
            </div>
            {availableRoles.map((role) => (
              <div key={role} className="flex items-center mb-2">
                <input
                  id={`role-${role}`}
                  name="roles[]"
                  type="checkbox"
                  checked={filters.roles.includes(role)}
                  onChange={() => handleFilterChange('roles', role)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor={`role-${role}`}
                  className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                >
                  {role}
                </label>
              </div>
            ))}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>

    {/* Date Filter - Keep existing */}
    <div className="py-4 px-4 w-full">
      <h3 className="flow-root">
        <div className="flex w-full items-center justify-between py-3 text-sm text-gray-600 hover:text-gray-500">
          <span className="font-medium text-gray-900 dark:text-gray-300">
            Date
          </span>
          <span className="flex items-center">
            <div className="flex items-center">
              <ReactDatePicker
                onChange={(dates) => handleDateRangeChange(dates as [Date | null, Date | null])}
                selectsRange
                isClearable
                showIcon
                showYearDropdown
                scrollableYearDropdown
                dropdownMode="select"
                startDate={currentStartDate}
                endDate={currentEndDate}
                icon={<CalendarDaysIcon className="text-gray-400" />}
                className="placeholder:text-gray-400 text-sm rounded-md border-none ring-1 ring-inset ring-gray-300 block w-[13rem] align-bottom cursor-pointer py-1 dark:bg-darkbg dark:ring-gray-500 dark:text-white"
                placeholderText="  Select period"
                calendarClassName="datepicker-calendar"
                wrapperClassName="datepicker-wrapper"
              />
            </div>
          </span>
        </div>
      </h3>
    </div>

  </div>
</aside>


        {/* Main content */}
        <main
          className={`${
            !isSidebarOpen
              ? "w-full"
              : "relative sm:transform translate-x-[19rem] sm:w-[calc(100%-19rem)]"
          } ${isRightSidebarOpen ? "pr-[19rem]" : ""} transition-all duration-200 ease-out`}
        >
          <div className="">
            {/* List */}
            {loading ? (
              <Loading />
            ) : (
              <>
                <div className="overflow-x-auto bg-white dark:bg-darkbglight shadow-sm ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 table-fixed">
                    <colgroup>
                      <col className="w-[2%]" />   {/* Checkbox */}
                      <col className="w-[12%]" />  {/* Date & Time */}
                      <col className="w-[14%]" />  {/* Email */}
                      <col className="w-[10%]" />  {/* Category */}
                      <col className="w-[14%]" />  {/* Role */}
                      <col className="w-[8%]" />   {/* Status */}
                      <col className="w-[38%]" />  {/* Description */}
                      <col className="w-[2%]" />   {/* Actions */}
                    </colgroup>
                    <thead className="bg-gray-50 dark:bg-darkbg">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-2 w-[2%]">
                          <input
                            type="checkbox"
                            checked={selectedIssues.length === issues.length && issues.length > 0}
                            onChange={handleSelectAll}
                            className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 pl-6 pr-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer"
                          onClick={() => handleSortChange("timestamp")}
                        >
                          <div className="flex items-center gap-1">
                            Date & Time
                            {filters.sortBy === "timestamp" && (
                              <span>{filters.sortOrder === "asc" ? "↑" : "↓"}</span>
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer"
                          onClick={() => handleSortChange("email")}
                        >
                          <div className="flex items-center gap-1">
                            Email
                            {filters.sortBy === "email" && (
                              <span>{filters.sortOrder === "asc" ? "↑" : "↓"}</span>
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer"
                          onClick={() => handleSortChange("title")}
                        >
                          <div className="flex items-center gap-1">
                            Category
                            {filters.sortBy === "title" && (
                              <span>{filters.sortOrder === "asc" ? "↑" : "↓"}</span>
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                        >
                          Role
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                        >
                          Description
                        </th>
                        <th scope="col" className="w-8">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-darkbglight">
                      {issues.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            No issues found
                          </td>
                        </tr>
                      ) : (
                        issues.map((issue, index) => (
                          <IssueRow
                            key={issue.id}
                            issue={issue}
                            onDelete={() => handleRequestDeleteIssue(issue)}
                            onStatusChange={(newStatus) =>
                              handleStatusChange(issue.id, newStatus)
                            }
                            selectedIssues={selectedIssues}
                            onSelect={handleSelect}
                            onShowInfo={(title, description) => {
                              setNotificationPopUp({ title, description, type: "info" });
                              setShowNotification(true);
                            }}
                            isLast={index === issues.length - 1}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing page {page} of {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="rounded-md bg-white dark:bg-darkbg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="rounded-md bg-white dark:bg-darkbg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Right sidebar */}
        <IssueRightSideBar
          isOpen={isRightSidebarOpen}
          onToggle={handleToggleRightSidebar}
          selectedCount={selectedIssues.length}
          onDeleteSelected={handleBulkDelete}
          onCloseSelected={handleBulkClose}
        />
      </div>

      <Notification
        show={showNotification}
        setShow={setShowNotification}
        {...notificationPopUp}
      />

      <ConfirmModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDeleteIssue}
        type="danger"
        title="Delete Issue"
        description={
          <span>
            Are you sure you want to delete this issue
            {issueToDelete ? (
              <>
                {" "}
                reported by{" "}
                <span className="font-semibold">{issueToDelete.email}</span> on{" "}
                <span className="font-semibold">
                  {new Date(issueToDelete.timestamp.endsWith("Z") ? issueToDelete.timestamp : issueToDelete.timestamp + "Z").toLocaleString()}
                </span>
                ?
              </>
            ) : (
              " ?"
            )}{" "}
            This action cannot be undone.
          </span>
        }
      />
    </div>
  );
}

export default Issues;